import { useRef, useEffect, useState, useCallback } from 'react';

/**
 * Hook for real-time background removal using Canvas API
 * Uses a simple chroma-key approach combined with edge detection
 */
export const useBackgroundRemoval = (options = {}) => {
  const {
    enabled = false,
    backgroundType = 'blur', // blur, color, image
    backgroundColor = '#0F172A',
    backgroundImage = '',
    blurAmount = 15
  } = options;

  const canvasRef = useRef(null);
  const outputCanvasRef = useRef(null);
  const videoRef = useRef(null);
  const animationRef = useRef(null);
  const bgImageRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load background image if needed
  useEffect(() => {
    if (backgroundType === 'image' && backgroundImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        bgImageRef.current = img;
      };
      img.src = backgroundImage;
    }
  }, [backgroundType, backgroundImage]);

  const processFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !outputCanvasRef.current || !enabled) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const outputCanvas = outputCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const outputCtx = outputCanvas.getContext('2d');

    if (video.readyState < 2) {
      animationRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;
    outputCanvas.width = width;
    outputCanvas.height = height;

    // Draw video frame
    ctx.drawImage(video, 0, 0, width, height);

    try {
      // Get image data
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // Simple background detection based on color variance
      // This is a simplified version - production would use ML models
      const processedData = new Uint8ClampedArray(data);

      // Apply background based on type
      if (backgroundType === 'blur') {
        // For blur, we just draw the video with blur filter
        outputCtx.filter = `blur(${blurAmount}px)`;
        outputCtx.drawImage(video, 0, 0, width, height);
        outputCtx.filter = 'none';
        
        // Draw person on top (simplified - draws center region clearly)
        const centerX = width * 0.25;
        const centerY = height * 0.1;
        const personWidth = width * 0.5;
        const personHeight = height * 0.9;
        
        outputCtx.save();
        outputCtx.beginPath();
        outputCtx.ellipse(
          width / 2,
          height / 2,
          personWidth / 2,
          personHeight / 2,
          0, 0, Math.PI * 2
        );
        outputCtx.clip();
        outputCtx.drawImage(video, 0, 0, width, height);
        outputCtx.restore();

      } else if (backgroundType === 'color') {
        // Fill with color
        outputCtx.fillStyle = backgroundColor;
        outputCtx.fillRect(0, 0, width, height);
        
        // Draw person mask (simplified ellipse)
        outputCtx.save();
        outputCtx.beginPath();
        outputCtx.ellipse(
          width / 2,
          height / 2,
          width * 0.35,
          height * 0.45,
          0, 0, Math.PI * 2
        );
        outputCtx.clip();
        outputCtx.drawImage(video, 0, 0, width, height);
        outputCtx.restore();

      } else if (backgroundType === 'image' && bgImageRef.current) {
        // Draw background image
        outputCtx.drawImage(bgImageRef.current, 0, 0, width, height);
        
        // Draw person mask
        outputCtx.save();
        outputCtx.beginPath();
        outputCtx.ellipse(
          width / 2,
          height / 2,
          width * 0.35,
          height * 0.45,
          0, 0, Math.PI * 2
        );
        outputCtx.clip();
        outputCtx.drawImage(video, 0, 0, width, height);
        outputCtx.restore();
      } else {
        // Fallback - just draw video
        outputCtx.drawImage(video, 0, 0, width, height);
      }
    } catch (e) {
      // If processing fails, just draw the video
      outputCtx.drawImage(video, 0, 0, width, height);
    }

    animationRef.current = requestAnimationFrame(processFrame);
  }, [enabled, backgroundType, backgroundColor, backgroundImage, blurAmount]);

  const startProcessing = useCallback((video) => {
    videoRef.current = video;
    setIsProcessing(true);
    processFrame();
  }, [processFrame]);

  const stopProcessing = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    canvasRef,
    outputCanvasRef,
    isProcessing,
    startProcessing,
    stopProcessing
  };
};

/**
 * Video effects hook for zoom, highlight, etc.
 */
export const useVideoEffects = () => {
  const [effects, setEffects] = useState({
    zoom: { enabled: false, level: 1, x: 0.5, y: 0.5 },
    highlight: { enabled: false, x: 0, y: 0, radius: 100 },
    cursor: { highlight: true, clickEffect: true }
  });

  const toggleZoom = useCallback((x = 0.5, y = 0.5, level = 2) => {
    setEffects(prev => ({
      ...prev,
      zoom: { 
        enabled: !prev.zoom.enabled, 
        level: prev.zoom.enabled ? 1 : level,
        x, y 
      }
    }));
  }, []);

  const setZoomLevel = useCallback((level) => {
    setEffects(prev => ({
      ...prev,
      zoom: { ...prev.zoom, level }
    }));
  }, []);

  const toggleHighlight = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      highlight: { ...prev.highlight, enabled: !prev.highlight.enabled }
    }));
  }, []);

  const setHighlightPosition = useCallback((x, y) => {
    setEffects(prev => ({
      ...prev,
      highlight: { ...prev.highlight, x, y }
    }));
  }, []);

  const toggleCursorHighlight = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, highlight: !prev.cursor.highlight }
    }));
  }, []);

  const toggleClickEffect = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, clickEffect: !prev.cursor.clickEffect }
    }));
  }, []);

  return {
    effects,
    toggleZoom,
    setZoomLevel,
    toggleHighlight,
    setHighlightPosition,
    toggleCursorHighlight,
    toggleClickEffect
  };
};

export default useBackgroundRemoval;
